import React from 'react';
import { 
  Plane, FileText, ShoppingCart, FileCheck, DollarSign, 
  HelpCircle, Info, Package, CalendarDays, CreditCard, ClipboardCheck,
  Truck, ShieldCheck, Briefcase, Tag, BarChart, Building, User,
  Clock, Banknote, Receipt, CreditCard as CardIcon, 
  Leaf, Map, FileWarning, PencilRuler, Stamp
} from 'lucide-react';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

// Icons for each document type
const documentTypeIcons = {
  'air waybill': Plane,
  'invoice': FileText,
  'purchase order': ShoppingCart,
  'end-user certificate': FileCheck,
  'quotation': DollarSign,
  'default': HelpCircle
};

// Helper to get a readable label for document types
const getDocumentTypeLabel = (type: string): string => {
  if (!type) return 'Unknown';
  // Convert camelCase or snake_case to readable format
  return type
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/^\w/, c => c.toUpperCase()); // Capitalize first letter
};

// Helper to get the correct key for document specific details
const getDetailKey = (documentType: string): string => {
  const typeMap: Record<string, string> = {
    'air waybill': 'airWaybill',
    'invoice': 'invoice',
    'purchase order': 'purchaseOrder',
    'end-user certificate': 'endUserCertificate',
    'quotation': 'quotation'
  };
  return typeMap[documentType?.toLowerCase()] || 'default';
};

interface DocumentTypeBadgeProps {
  documentType: string;
  documentDetails?: any; // The details from document extraction
  className?: string;
}

export const DocumentTypeBadge = ({ 
  documentType, 
  documentDetails, 
  className 
}: DocumentTypeBadgeProps) => {
  const [showDetails, setShowDetails] = React.useState(false);
  
  // Get the correct icon component or fallback to default
  const Icon = documentTypeIcons[documentType?.toLowerCase()] || documentTypeIcons.default;
  
  // Get a readable label
  const typeLabel = getDocumentTypeLabel(documentType);

  return (
    <>
      <button 
        onClick={() => setShowDetails(true)} 
        className={cn(
          "document-badge flex items-center justify-center w-8 h-8 rounded-full transition-all",
          "bg-transparent border border-primary text-primary hover:bg-primary/5",
          className
        )}
        aria-label={`${typeLabel} details`}
        title={typeLabel}
      >
        <Icon className="h-5 w-5" />
      </button>
      
      {/* Document details popup */}
      {showDetails && (
        <DocumentDetailsPopup 
          documentType={documentType}
          details={documentDetails}
          onClose={() => setShowDetails(false)}
        />
      )}
    </>
  );
};

interface DocumentDetailsPopupProps {
  documentType: string;
  details: any;
  onClose: () => void;
}

const DocumentDetailsPopup = ({ documentType, details, onClose }: DocumentDetailsPopupProps) => {
  // Get the readable document type label
  const typeLabel = getDocumentTypeLabel(documentType);
  
  // Get the icon for this document type
  const Icon = documentTypeIcons[documentType?.toLowerCase()] || documentTypeIcons.default;
  
  // --- Determine the source of details --- 
  const isHistoricalTimeline = !!details?.documentDetails; // Check if historical timeline data is present
  const timelineDetails = details?.documentDetails; // Extract timeline data if present
  const initialProcessingDetails = details; // Reference the top-level details for fallback

  // Get the type-specific details based on document type if available
  // Use the appropriate source based on whether it's historical or initial
  const typeDetails = isHistoricalTimeline 
    ? null // Timeline details don't have the same nested 'documentSpecificDetails' structure
    : initialProcessingDetails?.documentSpecificDetails?.[getDetailKey(documentType)];

  // Get transaction details (likely only present in initial processing data)
  const transactionDetails = initialProcessingDetails?.transactionDetails;
  // Get entity details (likely only present in initial processing data)
  const entities = initialProcessingDetails?.entities;
  // Get shipping details (likely only present in initial processing data)
  const shippingDetails = initialProcessingDetails?.shippingDetails;

  console.log("DocumentDetailsPopup received:", { 
    documentType, 
    details, 
    isHistoricalTimeline, 
    timelineDetails, 
    initialProcessingDetails, 
    typeDetails 
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pb-2 border-b">
          <div className="flex items-center gap-3 mb-1">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-transparent border border-primary">
              <Icon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl">{typeLabel}</DialogTitle>
              {/* Show ID from timeline or metadata */}
              {(timelineDetails?.documentId || initialProcessingDetails?.documentMetadata?.id) && (
                <DialogDescription className="text-sm opacity-80">
                  ID: {timelineDetails?.documentId || initialProcessingDetails.documentMetadata.id}
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>
        
        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4 py-4">
            {/* Display Timeline Details if available */}
            {isHistoricalTimeline && timelineDetails && (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                   <CalendarDays className="h-4 w-4 text-primary" />
                   <h4 className="text-sm font-medium text-primary">Timeline Information</h4>
                 </div>
                 <DetailItem label="Date" value={formatDate(timelineDetails.date)} />
                 <DetailItem label="Description" value={timelineDetails.description} />
                 <DetailItem label="Issuing Org" value={timelineDetails.issuingOrganization} />
                 <DetailItem label="Receiving Org" value={timelineDetails.receivingOrganization} />
                 {/* Add more relevant fields from timelineDetails if needed */}
              </div>
            )}

            {/* Display Type-Specific Details if NOT historical AND available */}
            {!isHistoricalTimeline && typeDetails && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-medium text-primary">Document Details</h4>
                </div>
                <DocumentTypeSpecificDetails type={documentType} details={typeDetails} />
              </div>
            )}
            
            {/* Transaction details if available (likely only initial processing) */}
            {!isHistoricalTimeline && transactionDetails?.items?.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-medium text-primary">Transaction Items</h4>
                </div>
                <TransactionDetailsTable items={transactionDetails.items} />
              </div>
            )}
            
            {/* Entities if available (likely only initial processing) */}
            {!isHistoricalTimeline && entities?.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-medium text-primary">Extracted Entities</h4>
                </div>
                <EntityList entities={entities} />
              </div>
            )}
            
            {/* Shipping details if available (likely only initial processing) */}
            {!isHistoricalTimeline && shippingDetails && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Truck className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-medium text-primary">Shipping Details</h4>
                </div>
                <ShippingDetailsDisplay details={shippingDetails} />
              </div>
            )}

            {/* Fallback message if no details were displayed */}
            {!isHistoricalTimeline && !typeDetails && !transactionDetails && !entities && !shippingDetails && (
              <p className="text-sm text-muted-foreground text-center py-4">No detailed information available for this document.</p>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// Helper to format date string
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  } catch (e) {
    return dateString; // Return original if parsing fails
  }
};

// Helper component for displaying key-value pairs
const DetailItem = ({ label, value }: { label: string, value: React.ReactNode }) => (
  <div className="flex items-center gap-2">
    <span className="font-medium">{label}:</span>
    <span className="text-sm">{value}</span>
  </div>
);

// Helper component to render type-specific details
const DocumentTypeSpecificDetails = ({ type, details }: { type: string, details: any }) => {
  if (!details) return null;
  
  switch(type.toLowerCase()) {
    case 'air waybill':
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {details.serviceType && (
              <>
          <span className="font-medium">Service Type:</span>
                <span>{details.serviceType}</span>
              </>
            )}
            
            {details.serviceCodes?.length > 0 && (
              <>
                <span className="font-medium">Service Codes:</span>
                <span>{details.serviceCodes.join(', ')}</span>
              </>
            )}
            
            {details.trackingNumber && (
              <>
          <span className="font-medium">Tracking:</span>
                <span>{details.trackingNumber}</span>
              </>
            )}
          
            {details.routingInformation && (
              <>
          <span className="font-medium">Routing:</span>
                <span>{details.routingInformation}</span>
              </>
            )}
            
            {details.originCode && (
              <>
                <span className="font-medium">Origin Code:</span>
                <span>{details.originCode}</span>
              </>
            )}
            
            {details.destinationCode && (
              <>
                <span className="font-medium">Destination Code:</span>
                <span>{details.destinationCode}</span>
              </>
            )}
            
            {details.handlingInstructions && (
              <>
                <span className="font-medium">Handling:</span>
                <span>{details.handlingInstructions}</span>
              </>
            )}
            
            {details.accountNumber && (
              <>
                <span className="font-medium">Account Number:</span>
                <span>{details.accountNumber}</span>
              </>
            )}
            
            {details.billType && (
              <>
                <span className="font-medium">Billing Type:</span>
                <span>{details.billType}</span>
              </>
            )}
            
            {details.packageMarks && (
              <>
                <span className="font-medium">Package Markings:</span>
                <span>{details.packageMarks}</span>
              </>
            )}
            
            {details.dangerousGoodsIndicator && (
              <>
                <span className="font-medium">Dangerous Goods:</span>
                <span>{details.dangerousGoodsIndicator}</span>
              </>
            )}
          </div>
          
          {details.customsInformation && Object.keys(details.customsInformation).length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Customs Information</h5>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pl-2">
                {details.customsInformation.customsDeclarationNumber && (
                  <>
                    <span className="font-medium">Declaration Number:</span>
                    <span>{details.customsInformation.customsDeclarationNumber}</span>
                  </>
                )}
                
                {details.customsInformation.customsValue && (
                  <>
                    <span className="font-medium">Customs Value:</span>
                    <span>{details.customsInformation.customsValue}</span>
                  </>
                )}
                
                {details.customsInformation.harmonizedCode && (
                  <>
                    <span className="font-medium">HS Code:</span>
                    <span>{details.customsInformation.harmonizedCode}</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      );
      
    case 'invoice':
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {details.invoiceType && (
              <>
                <span className="font-medium">Invoice Type:</span>
                <span>{details.invoiceType}</span>
              </>
            )}
            
            {details.paymentStatus && (
              <>
          <span className="font-medium">Payment Status:</span>
                <span>{details.paymentStatus}</span>
              </>
            )}
            
            {details.paymentReference && (
              <>
                <span className="font-medium">Payment Reference:</span>
                <span>{details.paymentReference}</span>
              </>
            )}
            
            {details.originalOrCopy && (
              <>
                <span className="font-medium">Original/Copy:</span>
                <span>{details.originalOrCopy}</span>
              </>
            )}
            
            {details.currencyExchangeInfo && (
              <>
                <span className="font-medium">Currency Exchange:</span>
                <span>{details.currencyExchangeInfo}</span>
              </>
            )}
            
            {details.paymentInstructions && (
              <>
                <span className="font-medium">Payment Instructions:</span>
                <span>{details.paymentInstructions}</span>
              </>
            )}
          </div>
          
          {details.bankingDetails && Object.keys(details.bankingDetails).length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Banking Details</h5>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pl-2">
                {details.bankingDetails.bankName && (
                  <>
                    <span className="font-medium">Bank:</span>
                    <span>{details.bankingDetails.bankName}</span>
                  </>
                )}
                
                {details.bankingDetails.accountName && (
                  <>
                    <span className="font-medium">Account Name:</span>
                    <span>{details.bankingDetails.accountName}</span>
                  </>
                )}
                
                {details.bankingDetails.accountNumber && (
                  <>
                    <span className="font-medium">Account Number:</span>
                    <span>{details.bankingDetails.accountNumber}</span>
                  </>
                )}
                
                {details.bankingDetails.swiftCode && (
                  <>
                    <span className="font-medium">SWIFT/BIC:</span>
                    <span>{details.bankingDetails.swiftCode}</span>
                  </>
                )}
                
                {details.bankingDetails.iban && (
                  <>
                    <span className="font-medium">IBAN:</span>
                    <span>{details.bankingDetails.iban}</span>
                  </>
                )}
                
                {details.bankingDetails.routingNumber && (
                  <>
                    <span className="font-medium">Routing Number:</span>
                    <span>{details.bankingDetails.routingNumber}</span>
                  </>
                )}
              </div>
            </div>
          )}
          
          {details.taxBreakdown && Object.keys(details.taxBreakdown).length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Tax Breakdown</h5>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pl-2">
                {details.taxBreakdown.taxType && (
                  <>
                    <span className="font-medium">Tax Type:</span>
                    <span>{details.taxBreakdown.taxType}</span>
                  </>
                )}
                
                {details.taxBreakdown.taxRate && (
                  <>
                    <span className="font-medium">Tax Rate:</span>
                    <span>{details.taxBreakdown.taxRate}</span>
                  </>
                )}
                
                {details.taxBreakdown.taxableAmount && (
                  <>
                    <span className="font-medium">Taxable Amount:</span>
                    <span>{details.taxBreakdown.taxableAmount}</span>
                  </>
                )}
                
                {details.taxBreakdown.taxAmount && (
                  <>
                    <span className="font-medium">Tax Amount:</span>
                    <span>{details.taxBreakdown.taxAmount}</span>
                  </>
                )}
              </div>
            </div>
          )}
          
          {details.discounts && Object.keys(details.discounts).length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Discounts</h5>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pl-2">
                {details.discounts.discountType && (
                  <>
                    <span className="font-medium">Discount Type:</span>
                    <span>{details.discounts.discountType}</span>
                  </>
                )}
                
                {details.discounts.discountRate && (
                  <>
                    <span className="font-medium">Discount Rate:</span>
                    <span>{details.discounts.discountRate}</span>
                  </>
                )}
                
                {details.discounts.discountAmount && (
                  <>
                    <span className="font-medium">Discount Amount:</span>
                    <span>{details.discounts.discountAmount}</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      );
      
    case 'purchase order':
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {details.poType && (
              <>
                <span className="font-medium">PO Type:</span>
                <span>{details.poType}</span>
              </>
            )}
            
            {details.requiredDeliveryDate && (
              <>
          <span className="font-medium">Delivery Date:</span>
                <span>{details.requiredDeliveryDate}</span>
              </>
            )}
            
            {details.specialInstructions && (
              <>
                <span className="font-medium">Instructions:</span>
                <span>{details.specialInstructions}</span>
              </>
            )}
            
            {details.shippingMethod && (
              <>
                <span className="font-medium">Shipping Method:</span>
                <span>{details.shippingMethod}</span>
              </>
            )}
            
            {details.packagingRequirements && (
              <>
                <span className="font-medium">Packaging Requirements:</span>
                <span>{details.packagingRequirements}</span>
              </>
            )}
            
            {details.termsAndConditions && (
              <>
                <span className="font-medium">Terms & Conditions:</span>
                <span>{details.termsAndConditions}</span>
              </>
            )}
            
            {details.deliverySchedule && (
              <>
                <span className="font-medium">Delivery Schedule:</span>
                <span>{details.deliverySchedule}</span>
              </>
            )}
          </div>
          
          {details.approvalDetails && Object.keys(details.approvalDetails).length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Approval Details</h5>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pl-2">
                {details.approvalDetails.approvedBy && (
                  <>
                    <span className="font-medium">Approved By:</span>
                    <span>{details.approvalDetails.approvedBy}</span>
                  </>
                )}
                
                {details.approvalDetails.approvalDate && (
                  <>
                    <span className="font-medium">Approval Date:</span>
                    <span>{details.approvalDetails.approvalDate}</span>
                  </>
                )}
                
                {details.approvalDetails.approvalSignature && (
                  <>
                    <span className="font-medium">Signature:</span>
                    <span>{details.approvalDetails.approvalSignature}</span>
                  </>
                )}
              </div>
            </div>
          )}
          
          {details.requestorInformation && Object.keys(details.requestorInformation).length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Requestor Information</h5>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pl-2">
                {details.requestorInformation.name && (
                  <>
                    <span className="font-medium">Name:</span>
                    <span>{details.requestorInformation.name}</span>
                  </>
                )}
                
                {details.requestorInformation.department && (
                  <>
                    <span className="font-medium">Department:</span>
                    <span>{details.requestorInformation.department}</span>
                  </>
                )}
                
                {details.requestorInformation.contactInfo && (
                  <>
                    <span className="font-medium">Contact:</span>
                    <span>{details.requestorInformation.contactInfo}</span>
                  </>
                )}
              </div>
            </div>
          )}
          
          {details.quality && Object.keys(details.quality).length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Quality Requirements</h5>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pl-2">
                {details.quality.requiredCertifications && (
                  <>
                    <span className="font-medium">Certifications:</span>
                    <span>{Array.isArray(details.quality.requiredCertifications) ? 
                      details.quality.requiredCertifications.join(', ') : 
                      details.quality.requiredCertifications}</span>
                  </>
                )}
                
                {details.quality.qualityInstructions && (
                  <>
                    <span className="font-medium">Instructions:</span>
                    <span>{details.quality.qualityInstructions}</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      );
      
    case 'end-user certificate':
      return (
        <div className="space-y-4">
          {details.aircraftInformation && Object.keys(details.aircraftInformation).length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Aircraft Information</h5>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pl-2">
                {details.aircraftInformation.model && (
                  <>
                    <span className="font-medium">Model:</span>
                    <span>{details.aircraftInformation.model}</span>
                  </>
                )}
                
                {details.aircraftInformation.registrationNumber && (
                  <>
                    <span className="font-medium">Registration:</span>
                    <span>{details.aircraftInformation.registrationNumber}</span>
                  </>
                )}
                
                {details.aircraftInformation.serialNumber && (
                  <>
                    <span className="font-medium">Serial Number:</span>
                    <span>{details.aircraftInformation.serialNumber}</span>
                  </>
                )}
                
                {details.aircraftInformation.equipmentModel && (
                  <>
                    <span className="font-medium">Equipment Model:</span>
                    <span>{details.aircraftInformation.equipmentModel}</span>
                  </>
                )}
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {details.usageType && (
              <>
                <span className="font-medium">Usage Type:</span>
                <span>{details.usageType}</span>
              </>
            )}
            
            {details.modifiedForMilitary && (
              <>
                <span className="font-medium">Military Modified:</span>
                <span>{details.modifiedForMilitary}</span>
              </>
            )}
            
            {details.countryOfRegistration && (
              <>
                <span className="font-medium">Country of Registration:</span>
                <span>{details.countryOfRegistration}</span>
              </>
            )}
            
            {details.countryOfOperation && (
              <>
                <span className="font-medium">Country of Operation:</span>
                <span>{details.countryOfOperation}</span>
              </>
            )}
            
            {details.exportControlClassification && (
              <>
                <span className="font-medium">Export Classification:</span>
                <span>{details.exportControlClassification}</span>
              </>
            )}
          </div>
          
          {details.endUserDeclarations && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">End User Declarations</h5>
              <p className="text-sm pl-2">{details.endUserDeclarations}</p>
            </div>
          )}
          
          {details.complianceStatements && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Compliance Statements</h5>
              <p className="text-sm pl-2">{details.complianceStatements}</p>
            </div>
          )}
          
          {details.authorizedUses && details.authorizedUses.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Authorized Uses</h5>
              <ul className="text-sm pl-4 list-disc">
                {details.authorizedUses.map((use: string, index: number) => (
                  <li key={index}>{use}</li>
                ))}
              </ul>
            </div>
          )}
          
          {details.resaleRestrictions && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Resale Restrictions</h5>
              <p className="text-sm pl-2">{details.resaleRestrictions}</p>
            </div>
          )}
          
          {details.signatureInformation && Object.keys(details.signatureInformation).length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Signature Information</h5>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pl-2">
                {details.signatureInformation.signatoryName && (
                  <>
                    <span className="font-medium">Name:</span>
                    <span>{details.signatureInformation.signatoryName}</span>
                  </>
                )}
                
                {details.signatureInformation.signatoryPosition && (
                  <>
                    <span className="font-medium">Position:</span>
                    <span>{details.signatureInformation.signatoryPosition}</span>
                  </>
                )}
                
                {details.signatureInformation.signatureDate && (
                  <>
                    <span className="font-medium">Date:</span>
                    <span>{details.signatureInformation.signatureDate}</span>
                  </>
                )}
                
                {details.signatureInformation.companyStamp && (
                  <>
                    <span className="font-medium">Company Stamp:</span>
                    <span>{details.signatureInformation.companyStamp}</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      );
      
    case 'quotation':
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {details.quoteNumber && (
              <>
                <span className="font-medium">Quote Number:</span>
                <span>{details.quoteNumber}</span>
              </>
            )}
            
            {details.estimatedLeadTime && (
              <>
                <span className="font-medium">Lead Time:</span>
                <span>{details.estimatedLeadTime}</span>
              </>
            )}
            
            {details.stockAvailability && (
              <>
                <span className="font-medium">Stock Availability:</span>
                <span>{details.stockAvailability}</span>
              </>
            )}
            
            {details.specialConditions && (
              <>
                <span className="font-medium">Special Conditions:</span>
                <span>{details.specialConditions}</span>
              </>
            )}
            
            {details.minimumOrderValue && (
              <>
                <span className="font-medium">Minimum Order Value:</span>
                <span>{details.minimumOrderValue}</span>
              </>
            )}
            
            {details.priceValidity && (
              <>
                <span className="font-medium">Price Validity:</span>
                <span>{details.priceValidity}</span>
              </>
            )}
            
            {details.quotePriority && (
              <>
                <span className="font-medium">Priority Level:</span>
                <span>{details.quotePriority}</span>
              </>
            )}
            
            {details.incoterms && (
              <>
                <span className="font-medium">Incoterms:</span>
                <span>{details.incoterms}</span>
              </>
            )}
          </div>
          
          {details.validityPeriod && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Validity Period</h5>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pl-2">
                {details.validityPeriod.startDate && (
                  <>
                    <span className="font-medium">Start Date:</span>
                    <span>{details.validityPeriod.startDate}</span>
                  </>
                )}
                
                {details.validityPeriod.endDate && (
                  <>
                    <span className="font-medium">End Date:</span>
                    <span>{details.validityPeriod.endDate}</span>
                  </>
                )}
                
                {details.validityPeriod.durationDays && (
                  <>
                    <span className="font-medium">Duration (Days):</span>
                    <span>{details.validityPeriod.durationDays}</span>
                  </>
                )}
              </div>
            </div>
          )}
          
          {details.alternativeOptions && details.alternativeOptions.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Alternative Options</h5>
              <div className="border rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Part Number</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Price</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Availability</th>
                    </tr>
                  </thead>
                  <tbody className="bg-background divide-y divide-border">
                    {details.alternativeOptions.map((option: any, index: number) => (
                      <tr key={index}>
                        <td className="px-3 py-2 text-xs">{option.partNumber || 'N/A'}</td>
                        <td className="px-3 py-2 text-xs">{option.price || 'N/A'}</td>
                        <td className="px-3 py-2 text-xs">{option.availability || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {details.deliveryOptions && details.deliveryOptions.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Delivery Options</h5>
              <div className="border rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Method</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Cost</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Timeframe</th>
                    </tr>
                  </thead>
                  <tbody className="bg-background divide-y divide-border">
                    {details.deliveryOptions.map((option: any, index: number) => (
                      <tr key={index}>
                        <td className="px-3 py-2 text-xs">{option.method || 'N/A'}</td>
                        <td className="px-3 py-2 text-xs">{option.cost || 'N/A'}</td>
                        <td className="px-3 py-2 text-xs">{option.timeframe || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {details.approverInformation && Object.keys(details.approverInformation).length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Approver Information</h5>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pl-2">
                {details.approverInformation.name && (
                  <>
                    <span className="font-medium">Name:</span>
                    <span>{details.approverInformation.name}</span>
                  </>
                )}
                
                {details.approverInformation.position && (
                  <>
                    <span className="font-medium">Position:</span>
                    <span>{details.approverInformation.position}</span>
                  </>
                )}
                
                {details.approverInformation.contactInfo && (
                  <>
                    <span className="font-medium">Contact:</span>
                    <span>{details.approverInformation.contactInfo}</span>
                  </>
                )}
              </div>
            </div>
          )}
          
          {details.remarks && (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground">Remarks</h5>
              <p className="text-sm pl-2">{details.remarks}</p>
            </div>
          )}
        </div>
      );
      
    default:
      return <p className="text-sm text-muted-foreground">No specific details available</p>;
  }
};

export default DocumentTypeBadge; 